import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { formatDateTime, randomString } from '/imports/utils';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Button } from '/imports/ui/Button';
import { Page } from '/imports/ui/Page';
import { modelClass, SupportTicket } from '/imports/api/supportTickets';
import { TextArea } from '/imports/ui/TextArea';
import { PageHeading } from '/imports/ui/PageHeading';
import { useFind, useMethod, useSubscription, useUser } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { SupportTicketMessages, modelClass as supportTicketMessageClass } from '/imports/api/supportTicketMessages';
import { PageBody } from '/imports/ui/PageBody';
import { FilesInput } from '/imports/ui/FilesInput';

const supportTicketExtraClasses = [
  '[&_.files-input_.target]:p-0',
  '[&_.files-input_tbody]:flex [&_.files-input_tbody]:gap-4',
  '[&_.field-group]:mb-4',
  '[&.not-new_.page-heading]:pb-0',
  '[&.not-new_.page-heading_h1]:leading-10',
  '[&.not-new_.page-heading_h1_span]:text-2xl [&.not-new_.page-heading_h1_span]:font-normal',
  '[&.not-new_.description]:rounded-card [&.not-new_.description]:border [&.not-new_.description]:border-grey-6 [&.not-new_.description]:p-4',
  '[&.not-new_.message]:rounded-card [&.not-new_.message]:border [&.not-new_.message]:border-grey-6 [&.not-new_.message]:p-4',
  '[&.not-new_.new-message]:flex-initial',
  '[&.not-new_.new-message_.page-heading]:hidden',
  '[&.not-new_.new-message_.page-body]:p-0',
  '[&.not-new_.messages]:flex [&.not-new_.messages]:flex-col [&.not-new_.messages]:gap-4',
  '[&.not-new_.message_.headers]:text-right [&.not-new_.message_.headers]:text-sm [&.not-new_.message_.headers]:text-grey-4',
  '[&.not-new_.files]:mt-2 [&.not-new_.files]:border-t [&.not-new_.files]:border-grey-6',
  '[&.not-new_.files_a]:ml-4',
  '[&.not-new_.reply-button]:mb-4',
  '[&_.related-tickets_h3]:mt-0 [&_.related-tickets_h3]:text-[red]',
  '[&_.related-tickets_table]:mb-8',
  '[&_.related-tickets_table_td]:p-4',
  '[&_.related-tickets_table_td:first-child]:pl-0',
].join(' ');

const SupportTicketPageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={`${props.className ? `${props.className} ` : ''}${supportTicketExtraClasses}`}
  />
);

export const SupportTicketPage = () => {
  const user = useUser();
  const userId = user?._id;
  const { _id } = useParams<any>();
  const isNew = _id === 'new';
  const defaultUpdate: Partial<SupportTicket> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<SupportTicket>>(defaultUpdate);
  const documentRef = useRef<SupportTicket>(undefined);
  const loadingMessages = isNew ? false : useSubscription(Publications.listSupportTicketMessages, { _id });
  const messages = useFind(() => SupportTicketMessages.find({ supportTicketId: _id }, { sort: { updatedAt: -1 } }), [loadingMessages]);
  const [ticket, loadingTicket] = isNew ? [undefined, false] : useMethod(modelClass.getMethod, _id as string, [_id]);
  const [renderKey, setRenderKey] = useState(randomString());
  const [replying, setReplying] = useState(false);
  const loading = loadingMessages || loadingTicket;
  if (loading) return <LoadingFlex />;
  if (isNew) {
    return <ModelFormPage
      modelClass={modelClass}
      styled={SupportTicketPageStyled}
      documentRef={documentRef}
      defaultUpdate={defaultUpdate}
      update={update}
      setUpdate={setUpdate}
      submitButtonText={strings.Submit()}
      formSpec={isNew ? [
        // [{ field: 'active', input: { type: Checkbox } }],
        [{ field: 'subject' }],
        [{ field: 'description', input: { type: TextArea } }],
        [{ field: 'files', input: { type: FilesInput } }],
      ] : []}
    />;
  }
  return (
    <SupportTicketPageStyled className="not-new">
      <PageHeading
        left={<h1>{strings.SupportCase()}: {ticket?.caseNumber}<br /><span>{ticket?.subject}</span></h1>}
      />
      <PageBody>
        {
          replying ?
            <ModelFormPage
              key={renderKey}
              className="new-message"
              modelClass={supportTicketMessageClass}
              defaultUpdate={{ _id: 'new', active: true, supportTicketId: _id }}
              formSpec={[
                [{ field: 'content', input: { type: TextArea }, label: strings.Message() }],
                [{ field: 'files', input: { type: FilesInput } }],
              ]}
              redirect={null}
              onSave={() => setRenderKey(randomString())}
              submitButtonText={strings.Submit()}
            />
            :
            <Button className="reply-button primary" onClick={() => setReplying(true)}>{strings.Reply()}</Button>
        }
        <div className="messages">
          {
            messages.map((m) => (
              <div key={m._id} className="message">
                <div className="headers">{m.createdByUserId === userId ? strings.You() : m.createdByName} @ {formatDateTime(m.updatedAt || new Date())}</div>
                <div className="content">{m.content}</div>
                {
                  m.files?.length ?
                    <div className="files">
                      {strings.Files()}: {m.files.map((f) => <a key={f.md5} href={f.url} target="_blank" rel="noreferrer">{f.name}</a>)}
                    </div>
                    :
                    null
                }
              </div>
            ))
          }
        </div>
        <div className="description">
          {ticket?.description}
          {
            ticket?.files?.length ?
              <div className="files">
                {strings.Files()}: {ticket.files.map((f) => <a key={f.md5} href={f.url} target="_blank" rel="noreferrer">{f.name}</a>)}
              </div>
              :
              null
          }
        </div>
      </PageBody>
    </SupportTicketPageStyled>
  );
};